import { useEffect, useState } from 'react'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { Box, Grid, Typography } from '@mui/material'

import Search from '../../components/search'
import SearchFilters from '../../components/search-filters'
import useDocumentTitle from '../../utils/hooks/useDocumentTitle'
import useSharedStyles from '../../utils/shared.styles'

import Results from './components/results'
import TabsComponent from './components/tabs'
import SearchResultsContext from './context'
import { getCurrentFilterCount, tabMatrix, tabMatrixByIndex, useFetchResults, useSearchFilters } from './helpers'
import useStyles from './index.styles'

const SearchResults = () => {
  const classes = useStyles
  const sharedClasses = useSharedStyles
  const { searchFilters, selectFilters, handleTabChange, q, tab } = useSearchFilters()

  const [tabIndex, setTabIndex] = useState<number>(tabMatrix[tab])

  const { isLoading, error, firstLoad, searchResults, hasMore, fetchMoreData } = useFetchResults({ tab, q, searchFilters })
  const [infiniteScrollRef] = useInfiniteScroll({
    loading: isLoading,
    hasNextPage: hasMore,
    onLoadMore: fetchMoreData,
    disabled: error,
    rootMargin: '0px 0px 200px 0px',
  })
  const numberOfResults = (searchResults ?? []).length

  const currentFilterCount = getCurrentFilterCount({ tab, searchFilters })

  useDocumentTitle('Meshify Central')

  // Change tabIndex if tab changes
  useEffect(() => {
    setTabIndex(tabMatrix[tab])
  }, [tab])

  // TODO: No results - need design
  // TODO: Search results screen when no search term - need design

  return (
    <SearchResultsContext.Provider value={{ searchResults, isLoading, error }}>
      <Grid container={true} sx={[sharedClasses.centerContentHorizontal, { marginBottom: 4 }]}>
        <div style={classes.searchBarContainer}>
          <Search />
        </div>
      </Grid>
      <Box sx={classes.searchResultContainer}>
        <Grid
          container={true}
          spacing={1}
          sx={{
            justifyContent: 'space-between',
            mt: '22px',
          }}
        >
          <TabsComponent tabIndex={tabIndex} handleTabChange={handleTabChange} />
        </Grid>

        <Grid container={true} direction="row" spacing={2}>
          <Grid
            size={{
              xs: 12,
              md: 8,
              lg: 9,
            }}
            sx={{
              order: { xs: 2, md: 1 },
            }}
          >
            <Box sx={classes.resultsContainer}>
              {!firstLoad && !isLoading && !hasMore && numberOfResults > 0 && (
                <Typography
                  variant="subtitle2"
                  sx={{
                    marginLeft: 2,
                    marginBottom: 1,
                  }}
                >
                  Showing {numberOfResults} result{numberOfResults === 1 ? '' : 's'} for "{q}"
                </Typography>
              )}
              <Results type={tabMatrixByIndex[tabIndex]} />
              {!firstLoad && !isLoading && hasMore && <div ref={infiniteScrollRef} />}
            </Box>
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 4,
              lg: 3,
            }}
            sx={[
              {
                order: { xs: 1, md: 2 },
              },
              classes.filterContainerGridItem,
            ]}
          >
            <Box sx={classes.filterContainer}>
              <SearchFilters tab={tab} searchFilters={searchFilters} selectFilters={selectFilters} hasActiveFilters={currentFilterCount > 0} />
            </Box>
          </Grid>
        </Grid>
      </Box>
    </SearchResultsContext.Provider>
  )
}

export default SearchResults
